import React from 'react';
import propTypes from 'prop-types';
import CarrierAvatar from './CarrierAvatar';
import CarrierInitialsAvatar from './CarrierInitialsAvatar';
import { cn } from 'lib/utils';
import { countOepStateUpdates } from 'helpers/oepUpdates';

// one row in the OEP Updates carrier sidebar. clicking it (or hitting enter on
// keyboard focus) selects the carrier via the parent's onSelect handler. the
// selected item shows a primary left border and light primary background.
// when the sidebar is collapsed only the initials avatar renders (never the
// icon), with the name as a tooltip and a primary ring marking the selection.
/**
 * @param carrier - normalized carrier from carrierFactory (needs name, carrierIcon, oepStateUpdates)
 * @param isSelected - whether this carrier is the one currently open
 * @param onSelect - called with the carrier name when the row is activated
 * @param buttonRef - forwarded to the underlying button so the parent can move
 *   keyboard focus during arrow-key nav
 * @param collapsed - render the avatar-only variant for the collapsed rail
 */
export default function CarrierSidebarItem({
  carrier,
  isSelected,
  onSelect,
  buttonRef,
  collapsed = false
}) {
  const { added, updatedPlus, updatedMinus, updatedMixed, removed } =
    countOepStateUpdates(carrier);
  const hasAnyBadge =
    added > 0 ||
    updatedPlus > 0 ||
    updatedMinus > 0 ||
    updatedMixed > 0 ||
    removed > 0;

  if (collapsed) {
    return (
      <li className='list-none'>
        <button
          ref={buttonRef}
          type='button'
          onClick={() => onSelect(carrier.name)}
          aria-current={isSelected ? 'true' : undefined}
          aria-label={carrier.name}
          title={carrier.name}
          className='flex w-full cursor-pointer items-center justify-center border-0 bg-transparent py-1.5 transition-colors hover:bg-primary-lighter focus:outline-none focus-visible:bg-primary-lighter'
        >
          <span
            className={cn(
              'flex shrink-0 rounded-full ring-offset-2 ring-offset-white',
              isSelected ? 'ring-2 ring-primary' : 'ring-0'
            )}
          >
            <CarrierInitialsAvatar name={carrier.name} size={32} />
          </span>
        </button>
      </li>
    );
  }

  return (
    <li className='list-none'>
      <button
        ref={buttonRef}
        type='button'
        onClick={() => onSelect(carrier.name)}
        aria-current={isSelected ? 'true' : undefined}
        className={cn(
          'flex w-full items-center gap-3 border-0 border-l-3 border-transparent bg-transparent px-3 py-2.5 text-left cursor-pointer',
          'transition-colors hover:bg-primary-lighter focus:outline-none focus-visible:bg-primary-lighter focus-visible:ring-1 focus-visible:ring-primary',
          isSelected && 'border-l-primary bg-primary-light'
        )}
      >
        <CarrierAvatar carrier={carrier} />

        <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
          <span
            className={cn(
              'truncate text-sm font-semibold',
              isSelected ? 'text-primary-darkened' : 'text-gray-800'
            )}
          >
            {carrier.name}
          </span>
          {hasAnyBadge ? (
            <span className='flex flex-wrap items-center gap-1.5'>
              {added > 0 && (
                <span
                  className='inline-flex items-center rounded-md bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-800'
                  title={`${added} new ${added === 1 ? 'state' : 'states'}`}
                >
                  +{added}
                </span>
              )}
              {updatedPlus > 0 && (
                <span
                  className='inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800'
                  title={`${updatedPlus} ${updatedPlus === 1 ? 'state' : 'states'} with new counties`}
                >
                  +{updatedPlus}
                </span>
              )}
              {updatedMinus > 0 && (
                <span
                  className='inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800'
                  title={`${updatedMinus} ${updatedMinus === 1 ? 'state' : 'states'} losing counties`}
                >
                  −{updatedMinus}
                </span>
              )}
              {updatedMixed > 0 && (
                <span
                  className='inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800'
                  title={`${updatedMixed} ${updatedMixed === 1 ? 'state' : 'states'} with both county additions and exits`}
                >
                  ±{updatedMixed}
                </span>
              )}
              {removed > 0 && (
                <span
                  className='inline-flex items-center rounded-md bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-800'
                  title={`${removed} ${removed === 1 ? 'state' : 'states'} removed`}
                >
                  −{removed}
                </span>
              )}
            </span>
          ) : (
            <span className='text-xs italic text-gray-400'>
              No updates yet
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

CarrierSidebarItem.propTypes = {
  carrier: propTypes.object.isRequired,
  isSelected: propTypes.bool,
  onSelect: propTypes.func.isRequired,
  buttonRef: propTypes.oneOfType([
    propTypes.func,
    propTypes.shape({ current: propTypes.any })
  ]),
  collapsed: propTypes.bool
};
